/**
 * useStaggeredTestPhase — shared phase machine for the "Test" affordances
 * inside ProductDataLookupCard. Owns the four-phase state, the timer refs,
 * and the canonical motion ordering documented in parent
 * `47-02-3-phase-b-PLAN.md` §9.4 v3.1 P0-3:
 *
 *   t=0       — caller transitions to `fetching` (spinner + disabled)
 *   t=resp    — caller invokes `showWithMotion(onMount)` after server returns
 *   t=resp+0  — spinner fades out (150ms) — UI sees `phase === 'cooling'`
 *   t=resp+50 — `onMount` callback fires (caller mounts the result band);
 *               phase → `showing` so the result band's animate-in classes
 *               trigger the 200ms fade+slide
 *   t=resp+200 — phase → `idle`; button + input re-enable, animation done
 *
 * Both the bulk TestButton (ProductDataLookupCard.tsx) and the per-SKU
 * SkuTestSection consume this hook so their timings can never drift. Earlier
 * drafts duplicated the three constants + the timer logic across both
 * components, which the architect review flagged as the only real
 * architectural debt in the follow-up PR (Q7).
 *
 * `prefers-reduced-motion` is honored by Tailwind's `motion-safe:` /
 * `motion-reduce:` utility classes the consumers apply to the result band
 * — this hook's timing constants don't change in reduced-motion mode (the
 * setTimeouts still drive the phase machine; the visual fade is what's
 * suppressed). Callers should always apply paired `motion-reduce:animate-none`
 * classes to honor the OS preference.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// Motion timings (parent PLAN §9.4 v3.1 P0-3). Single source of truth —
// importing these here keeps the spinner-out → stagger → band-in → re-enable
// sequence consistent across every Test affordance in this Card.
export const MOTION_SPINNER_FADE_MS = 150;
export const MOTION_STAGGER_MS = 50;
export const MOTION_BAND_FADE_MS = 200;

export type TestPhase = 'idle' | 'fetching' | 'cooling' | 'showing';

export interface UseStaggeredTestPhaseReturn {
  /** Current phase. Drives the consumer's spinner / band-visibility state. */
  phase: TestPhase;
  /**
   * Transition to `fetching` (cancels any pending timers from a prior
   * sequence). Call this when the user clicks "Test".
   */
  start: () => void;
  /**
   * Cancel any in-flight phase timers and snap back to `idle`. Use for
   * error paths or "Clear result" affordances.
   */
  reset: () => void;
  /**
   * Run the response-side motion sequence: cool the spinner, mount the
   * band, re-enable the button. `onMount` is invoked at the stagger
   * boundary (t = SPINNER_FADE + STAGGER) so the consumer can set its
   * result state right before the band renders.
   */
  showWithMotion: (onMount: () => void) => void;
}

export function useStaggeredTestPhase(): UseStaggeredTestPhaseReturn {
  const [phase, setPhase] = useState<TestPhase>('idle');
  const phaseTimers = useRef<number[]>([]);

  // Cancel any in-flight timers on unmount so a stale transition can't
  // fire setState on a torn-down component.
  useEffect(() => {
    const timers = phaseTimers.current;
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, []);

  const cancelTimers = useCallback(() => {
    for (const t of phaseTimers.current) clearTimeout(t);
    phaseTimers.current = [];
  }, []);

  const start = useCallback(() => {
    cancelTimers();
    setPhase('fetching');
  }, [cancelTimers]);

  const reset = useCallback(() => {
    cancelTimers();
    setPhase('idle');
  }, [cancelTimers]);

  const showWithMotion = useCallback(
    (onMount: () => void) => {
      cancelTimers();
      setPhase('cooling');
      const t1 = window.setTimeout(() => {
        onMount();
        setPhase('showing');
      }, MOTION_SPINNER_FADE_MS + MOTION_STAGGER_MS);
      const t2 = window.setTimeout(() => {
        setPhase('idle');
      }, MOTION_SPINNER_FADE_MS + MOTION_STAGGER_MS + MOTION_BAND_FADE_MS);
      phaseTimers.current = [t1, t2];
    },
    [cancelTimers],
  );

  return { phase, start, reset, showWithMotion };
}
